/**
 * Product Variant Image Filter & Mobile Slider
 * Handles variant-specific image filtering and mobile slider functionality
 * Optimized version with performance improvements and bug fixes
 */
class ProductImageSlider {
  constructor() {
    this.mediaSlides = null;
    this.productMediaList = null;
    this.paginationContainer = null;
    this.prevButton = null;
    this.nextButton = null;
    this.isMobile = window.innerWidth < 750;
    this.currentSlideIndex = 0;
    this.currentVariantId = null;
    this.cachedVisibleSlides = null;
    this.allVariantOptions = null;
    this.hasVariantSpecificImages = null; // Cache this result
    this.colorGrouping = false; // Set from the gallery's data-color-grouping attribute
    this.resizeTimeout = null;
  }
  
  init() {
    // Cache DOM elements
    this.mediaSlides = document.querySelectorAll('.product__slide');
    this.productMediaList = document.getElementById('ProductMediaList');
    this.paginationContainer = document.getElementById('ProductSliderPagination');
    this.prevButton = document.querySelector('.slider-arrow--prev');
    this.nextButton = document.querySelector('.slider-arrow--next');
    
    if (!this.productMediaList || this.mediaSlides.length === 0) {
      console.warn('Product media elements not found');
      return;
    }
    
    this.colorGrouping = document.getElementById('MainProductGallery')?.dataset.colorGrouping === 'true';
    this.setCurrentVariant();
    this.calculateVariantSpecificImages(); // Calculate once
    
    if (this.isMobile) {
      this.setupMobileSlider();
    } else {
      this.hideMobileElements(); // Ensure mobile elements are hidden on desktop
    }
    
    this.bindVariantChangeEvents();
    this.bindResizeEvents();
    
    this.filterImagesByCurrentVariant();
  }
  
  hideMobileElements() {
    // Hide arrows and pagination on desktop
    if (this.prevButton) this.prevButton.style.display = 'none';
    if (this.nextButton) this.nextButton.style.display = 'none';
    if (this.paginationContainer) {
      this.paginationContainer.innerHTML = '';
      this.paginationContainer.style.display = 'none';
    }
  }
  
  setupMobileSlider() {
    if (!this.isMobile) return;
    
    this.updateMobilePagination();
    this.setupScrollListener();
    this.setupSwipeEvents();
    this.bindArrowEvents();
  }
  
  /**
   * Get visible slides, filtering out duplicates and hidden slides
   * Optimized with early returns and better duplicate detection
   */
  getVisibleSlides() {
    if (this.cachedVisibleSlides) {
      return this.cachedVisibleSlides;
    }

    const visible = [];
    const seenContents = new Set();
    
    for (const slide of this.mediaSlides) {
      // Skip if hidden by variant filtering
      if (slide.classList.contains('variant-image-hidden')) {
        continue;
      }
      
      // Skip if display is none
      const computedStyle = window.getComputedStyle(slide);
      if (computedStyle.display === 'none') {
        continue;
      }
      
      // Skip if media--hidden class is present (Shopify's default hidden class)
      if (slide.classList.contains('media--hidden')) {
        continue;
      }
      
      // Skip duplicates by checking data attributes first (faster than checking src)
      const mediaId = slide.dataset.mediaId;
      if (mediaId && seenContents.has(mediaId)) {
        continue;
      }
      
      // Then check image src as fallback
      const img = slide.querySelector('img');
      if (img && img.src) {
        const slideContent = img.src.split('?')[0];
        if (slideContent && seenContents.has(slideContent)) {
          continue;
        }
        if (slideContent) {
          seenContents.add(slideContent);
        }
      }
      
      if (mediaId) {
        seenContents.add(mediaId);
      }
      
      visible.push(slide);
    }
    
    this.cachedVisibleSlides = visible;
    return this.cachedVisibleSlides;
  }
  
  updateMobilePagination() {
    if (!this.paginationContainer || !this.isMobile) return;
    
    const visibleSlides = this.getVisibleSlides();
    
    // Don't show pagination if only 1 slide
    if (visibleSlides.length <= 1) {
      this.paginationContainer.textContent = '';
      this.paginationContainer.style.display = 'none';
      if (this.prevButton) this.prevButton.style.display = 'none';
      if (this.nextButton) this.nextButton.style.display = 'none';
      return;
    }
    
    this.paginationContainer.style.display = 'flex';
    this.updatePaginationCounter(this.currentSlideIndex, visibleSlides.length);
    
    this.updateArrowButtons(visibleSlides.length);
  }

  updatePaginationCounter(currentIndex, totalSlides) {
    if (!this.paginationContainer) return;

    this.paginationContainer.textContent = `${currentIndex + 1} / ${totalSlides}`;
  }
  
  updateArrowButtons(visibleSlideCount) {
    if (!this.prevButton || !this.nextButton || !this.isMobile) return;
    
    if (visibleSlideCount > 1) {
      this.prevButton.style.display = 'flex';
      this.nextButton.style.display = 'flex';
      
      this.prevButton.disabled = this.currentSlideIndex === 0;
      this.nextButton.disabled = this.currentSlideIndex === visibleSlideCount - 1;
    } else {
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
    }
  }
  
  bindArrowEvents() {
    if (!this.prevButton || !this.nextButton) return;
    
    // Remove existing listeners to prevent duplicates
    this.prevButton.replaceWith(this.prevButton.cloneNode(true));
    this.nextButton.replaceWith(this.nextButton.cloneNode(true));
    
    // Re-select buttons after clone
    this.prevButton = document.querySelector('.slider-arrow--prev');
    this.nextButton = document.querySelector('.slider-arrow--next');
    
    this.prevButton.addEventListener('click', () => {
      const visibleSlides = this.getVisibleSlides();
      this.goToSlide(this.currentSlideIndex - 1, visibleSlides.length);
    });
    
    this.nextButton.addEventListener('click', () => {
      const visibleSlides = this.getVisibleSlides();
      this.goToSlide(this.currentSlideIndex + 1, visibleSlides.length);
    });
  }
  
  setupScrollListener() {
    if (!this.productMediaList) return;
    
    // Use passive listener for better performance
    this.productMediaList.addEventListener('scroll', () => {
      this.updateActiveDotFromScroll();
    }, { passive: true });
  }
  
  updateActiveDotFromScroll() {
    if (!this.productMediaList || !this.paginationContainer) return;
    
    const visibleSlides = this.getVisibleSlides();
    if (visibleSlides.length <= 1) return;
    
    const scrollLeft = this.productMediaList.scrollLeft;
    const slideWidth = this.productMediaList.clientWidth;
    
    // Use Math.floor for more accurate index calculation
    const currentIndex = Math.max(0, Math.min(
      Math.floor(scrollLeft / slideWidth + 0.5),
      visibleSlides.length - 1
    ));
    
    if (currentIndex !== this.currentSlideIndex) {
      this.currentSlideIndex = currentIndex;
      this.updatePaginationCounter(currentIndex, visibleSlides.length);
      this.updateArrowButtons(visibleSlides.length);
    }
  }
  
  setupSwipeEvents() {
    if (!this.productMediaList || !this.isMobile) return;
    
    let startX = 0;
    let isSwiping = false;
    let startTime = 0;
    
    this.productMediaList.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startTime = Date.now();
      isSwiping = true;
    }, { passive: true });
    
    this.productMediaList.addEventListener('touchmove', (e) => {
      if (!isSwiping) return;
      
      // Prevent default to stop page scroll during horizontal swipe
      const currentX = e.touches[0].clientX;
      const diff = startX - currentX;
      
      // If mainly horizontal swipe, prevent vertical scroll
      if (Math.abs(diff) > 10) {
        e.preventDefault();
      }
    }, { passive: false });
    
    this.productMediaList.addEventListener('touchend', (e) => {
      if (!isSwiping) return;
      
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      const timeDiff = Date.now() - startTime;
      const velocity = Math.abs(diff) / timeDiff;
      
      // Dynamic threshold based on velocity
      const threshold = Math.max(30, Math.min(80, 50 * velocity));
      
      if (Math.abs(diff) > threshold) {
        const visibleSlides = this.getVisibleSlides();
        if (diff > 0 && this.currentSlideIndex < visibleSlides.length - 1) {
          this.goToSlide(this.currentSlideIndex + 1, visibleSlides.length);
        } else if (diff < 0 && this.currentSlideIndex > 0) {
          this.goToSlide(this.currentSlideIndex - 1, visibleSlides.length);
        }
      }
      
      isSwiping = false;
    }, { passive: true });
  }
  
  goToSlide(index, totalVisibleSlides) {
    if (!this.productMediaList || !this.isMobile) return;
    
    if (!totalVisibleSlides) {
      const visibleSlides = this.getVisibleSlides();
      totalVisibleSlides = visibleSlides.length;
    }
    
    // Validate index
    index = Math.max(0, Math.min(index, totalVisibleSlides - 1));
    
    if (index === this.currentSlideIndex) return;
    
    this.currentSlideIndex = index;
    const slideWidth = this.productMediaList.clientWidth;
    
    // Use requestAnimationFrame for smoother animation
    requestAnimationFrame(() => {
      this.productMediaList.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth'
      });
    });
    
    this.updatePaginationCounter(index, totalVisibleSlides);
    
    // Update arrows
    this.updateArrowButtons(totalVisibleSlides);
  }
  
  bindResizeEvents() {
    // Debounce resize event
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 150);
    });
  }
  
  handleResize() {
    const newIsMobile = window.innerWidth < 750;
    
    if (newIsMobile !== this.isMobile) {
      this.isMobile = newIsMobile;
      this.cachedVisibleSlides = null;
      
      if (this.isMobile) {
        this.setupMobileSlider();
        // Reset to first slide on mobile
        this.currentSlideIndex = 0;
        if (this.productMediaList) {
          this.productMediaList.scrollTo({ left: 0, behavior: 'instant' });
        }
      } else {
        this.hideMobileElements();
      }
      
      // Re-filter images after resize
      this.filterImagesByCurrentVariant();
    }
  }
  
  setCurrentVariant() {
    const variantInput = document.querySelector('input[name="id"]');
    const variantSelect = document.querySelector('select[name="id"]');
    
    if (variantInput && variantInput.value) {
      this.currentVariantId = parseInt(variantInput.value);
    } else if (variantSelect && variantSelect.value) {
      this.currentVariantId = parseInt(variantSelect.value);
    } else if (window.productVariants && Object.keys(window.productVariants).length > 0) {
      // Get first available variant
      const firstAvailable = Object.values(window.productVariants).find(v => v.available);
      this.currentVariantId = firstAvailable ? firstAvailable.id : Object.keys(window.productVariants)[0];
    }
  }
  
  calculateVariantSpecificImages() {
    if (!window.productVariants) {
      this.hasVariantSpecificImages = false;
      this.allVariantOptions = new Set();
      return;
    }
    
    // Calculate all variant options once
    this.allVariantOptions = new Set();
    Object.values(window.productVariants).forEach(variant => {
      if (variant.option1) {
        this.allVariantOptions.add(variant.option1.toLowerCase().trim());
      }
      if (variant.option2) {
        this.allVariantOptions.add(variant.option2.toLowerCase().trim());
      }
      if (variant.option3) {
        this.allVariantOptions.add(variant.option3.toLowerCase().trim());
      }
    });
    
    // Colour grouping: each slide carries its colour in data-media-color (set in Liquid from the alt text)
    if (this.colorGrouping) {
      this.hasVariantSpecificImages = Array.from(this.mediaSlides).some(slide => slide.dataset.mediaColor);
      return;
    }

    // Check if product has variant-specific images
    this.hasVariantSpecificImages = false;
    for (const slide of this.mediaSlides) {
      const slideAlt = slide.dataset.mediaAlt || '';
      const slideAltLower = slideAlt.toLowerCase().trim();
      
      if (slideAlt && slideAlt !== '' && this.allVariantOptions.has(slideAltLower)) {
        this.hasVariantSpecificImages = true;
        break; // No need to check further
      }
    }
  }
  
  bindVariantChangeEvents() {
    // Remove existing listeners to prevent duplicates
    const variantSelects = document.querySelectorAll('select[name="id"], input[name="id"]');
    variantSelects.forEach(select => {
      select.removeEventListener('change', this.handleVariantChange);
    });
    
    // Add new listeners
    variantSelects.forEach(select => {
      select.addEventListener('change', this.handleVariantChange.bind(this));
    });
    
    // Shopify theme events
    if (window.theme?.PUB_SUB_EVENTS) {
      document.removeEventListener(window.theme.PUB_SUB_EVENTS.variantChange, this.handleThemeVariantChange);
      document.addEventListener(window.theme.PUB_SUB_EVENTS.variantChange, this.handleThemeVariantChange.bind(this));
    }
    
    // Custom variant change events
    document.removeEventListener('variant:change', this.handleCustomVariantChange);
    document.addEventListener('variant:change', this.handleCustomVariantChange.bind(this));
  }
  
  handleVariantChange(e) {
    const variantId = parseInt(e.target.value);
    if (variantId && variantId !== this.currentVariantId) {
      this.currentVariantId = variantId;
      this.filterImagesByCurrentVariant();
    }
  }
  
  handleThemeVariantChange(event) {
    if (event.detail?.id && event.detail.id !== this.currentVariantId) {
      this.currentVariantId = event.detail.id;
      this.filterImagesByCurrentVariant();
    }
  }
  
  handleCustomVariantChange(event) {
    if (event.detail?.variant?.id && event.detail.variant.id !== this.currentVariantId) {
      this.currentVariantId = event.detail.variant.id;
      this.filterImagesByCurrentVariant();
    }
  }
  
  filterImagesByCurrentVariant() {
    if (!this.currentVariantId) return;
    
    const variant = window.productVariants?.[this.currentVariantId];
    if (!variant) return;
    
    this.filterImagesByVariant(variant);
  }
  
  /**
   * Optimized filtering logic with caching
   */
  filterImagesByVariant(variant) {
    const variantColor = this.getVariantColor(variant);
    const variantColorLower = variantColor ? variantColor.toLowerCase().trim() : '';
    
    // Use cached result for variant-specific images check
    const hasVariantSpecificImages = this.hasVariantSpecificImages;
    
    // Batch DOM updates
    const updates = [];

    if (this.colorGrouping) {
      // Same rules as before, but the colour comes from data-media-color (the alt text may now carry SEO text).
      // - No image matches any colour: show everything like a standard theme.
      // - Otherwise show images of the selected colour plus images with no alt.
      // - If nothing matches the selected colour: show the no-alt images, or everything if there are none.
      const slidesArray = Array.from(this.mediaSlides);
      const hasAnyColor = slidesArray.some(slide => slide.dataset.mediaColor);
      const hasThisColor = slidesArray.some(slide => slide.dataset.mediaColor === variantColorLower);
      const hasNoAltImages = slidesArray.some(slide => !slide.dataset.mediaAlt);
      for (const slide of slidesArray) {
        const slideColor = slide.dataset.mediaColor || '';
        const hasAlt = !!slide.dataset.mediaAlt;
        let shouldShow;
        if (!hasAnyColor) {
          shouldShow = true;
        } else if (hasThisColor) {
          shouldShow = slideColor === variantColorLower || !hasAlt;
        } else {
          shouldShow = hasNoAltImages ? !hasAlt : true;
        }
        updates.push({ slide, shouldShow });
      }
    }

    for (const slide of this.colorGrouping ? [] : this.mediaSlides) {
      const slideAlt = slide.dataset.mediaAlt || '';
      const slideAltLower = slideAlt.toLowerCase().trim();
      let shouldShow = false;
      
      if (hasVariantSpecificImages) {
        // For variant-specific products: Show only images that match current variant OR have no alt tag
        shouldShow = slideAltLower === variantColorLower || slideAlt === '';
      } else {
        // For standard products: Show ALL images (no filtering)
        shouldShow = true;
      }
      
      updates.push({ slide, shouldShow });
    }
    
    // Apply all updates at once
    updates.forEach(({ slide, shouldShow }) => {
      slide.classList.toggle('variant-image-hidden', !shouldShow);
    });
    
    this.cachedVisibleSlides = null;

    // Update mobile slider if needed
    if (this.isMobile) {
      this.currentSlideIndex = 0;
      this.updateMobilePagination();
      
      // Scroll to first slide with delay for smooth transition
      setTimeout(() => {
        if (this.productMediaList) {
          this.productMediaList.scrollTo({ left: 0, behavior: 'smooth' });
        }
      }, 50);
    }
  }
  
  getVariantColor(variant) {
    // Get from option1 (color is usually the first option)
    if (variant.option1) {
      return variant.option1.trim();
    }
    
    return null;
  }
  
  // Clean up method for when component is destroyed
  destroy() {
    clearTimeout(this.resizeTimeout);
    
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
    
    if (this.productMediaList) {
      this.productMediaList.removeEventListener('scroll', this.updateActiveDotFromScroll);
      this.productMediaList.removeEventListener('touchstart', this.setupSwipeEvents);
      this.productMediaList.removeEventListener('touchend', this.setupSwipeEvents);
    }
    
    if (this.prevButton) {
      this.prevButton.replaceWith(this.prevButton.cloneNode(true));
    }
    
    if (this.nextButton) {
      this.nextButton.replaceWith(this.nextButton.cloneNode(true));
    }
  }
}

// Singleton instance to prevent multiple initializations
let productImageSliderInstance = null;

function initializeProductImageSlider() {
  const productGallery = document.getElementById('MainProductGallery');
  if (!productGallery) return;
  
  // Destroy existing instance if it exists
  if (productImageSliderInstance) {
    productImageSliderInstance.destroy();
  }
  
  productImageSliderInstance = new ProductImageSlider();
  
  productImageSliderInstance.init();
}

// Initialize based on DOM ready state
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeProductImageSlider);
} else {
  initializeProductImageSlider();
}

// Reinitialize for Shopify section loads with debouncing
if (typeof Shopify !== 'undefined') {
  let sectionLoadTimeout;
  document.addEventListener('shopify:section:load', function(event) {
    clearTimeout(sectionLoadTimeout);
    sectionLoadTimeout = setTimeout(() => {
      if (event.target.querySelector('#MainProductGallery') || 
          event.target.closest('#MainProductGallery')) {
        initializeProductImageSlider();
      }
    }, 100);
  });
  
  // Also handle section select in editor
  document.addEventListener('shopify:section:select', function(event) {
    if (event.target.querySelector('#MainProductGallery') || 
        event.target.closest('#MainProductGallery')) {
      initializeProductImageSlider();
    }
  });
}

// Export for debugging if needed
if (typeof window !== 'undefined') {
  window.ProductImageSlider = ProductImageSlider;
}